import { ref, computed } from '@vue/composition-api'
import { memberGuid as defaultGetRowId } from './memberRowId'

export default function useMultiSelectTable({
  tableItems,
  totalUsers,
  filteredFullList,
  isClientPaginatedMode,
  getRowId = defaultGetRowId,
}) {
  const selectedMap = ref({})
  const allMatchingSelected = ref(false)
  const isSelectingAllMatching = ref(false)

  const selectedIds = computed(() => Object.keys(selectedMap.value))
  const selectedCount = computed(() => selectedIds.value.length)

  const pageIds = computed(() => (
    tableItems.value.map(item => getRowId(item)).filter(Boolean)
  ))

  const isAllSelectedOnPage = computed(() => {
    if (!pageIds.value.length) return false
    return pageIds.value.every(id => selectedMap.value[id])
  })

  const canSelectAllMatching = computed(() => (
    !allMatchingSelected.value
    && isAllSelectedOnPage.value
    && totalUsers.value > tableItems.value.length
  ))

  function isRowSelected(item) {
    const id = getRowId(item)
    return id ? !!selectedMap.value[id] : false
  }

  function toggleRow(item) {
    const id = getRowId(item)
    if (!id) return
    const next = { ...selectedMap.value }
    if (next[id]) delete next[id]
    else next[id] = item
    selectedMap.value = next
    allMatchingSelected.value = false
  }

  function toggleSelectAllOnPage() {
    const next = { ...selectedMap.value }
    if (isAllSelectedOnPage.value) {
      pageIds.value.forEach(id => delete next[id])
    } else {
      tableItems.value.forEach(item => {
        const id = getRowId(item)
        if (id) next[id] = item
      })
    }
    selectedMap.value = next
    allMatchingSelected.value = false
  }

  function clearSelection() {
    selectedMap.value = {}
    allMatchingSelected.value = false
  }

  function setSingleSelection(payload) {
    if (payload) {
      const id = getRowId(payload)
      selectedMap.value = id ? { [id]: payload } : {}
    } else {
      selectedMap.value = {}
    }
  }

  function applySelection(list) {
    const next = { ...selectedMap.value }
    ;(list || []).forEach(item => {
      const id = getRowId(item)
      if (id) next[id] = item
    })
    selectedMap.value = next
    allMatchingSelected.value = true
  }

  function selectAllMatching({ fetchRemote, onError }) {
    if (isSelectingAllMatching.value) return

    if (isClientPaginatedMode.value) {
      applySelection(filteredFullList.value)
      return
    }

    if (!fetchRemote) return

    isSelectingAllMatching.value = true
    fetchRemote()
      .then(applySelection)
      .catch(() => {
        if (onError) onError()
      })
      .finally(() => {
        isSelectingAllMatching.value = false
      })
  }

  function getSelectedMembers() {
    return Object.values(selectedMap.value)
  }

  return {
    selectedMap,
    allMatchingSelected,
    isSelectingAllMatching,
    selectedIds,
    selectedCount,
    pageIds,
    isAllSelectedOnPage,
    canSelectAllMatching,
    isRowSelected,
    toggleRow,
    toggleSelectAllOnPage,
    clearSelection,
    setSingleSelection,
    selectAllMatching,
    getSelectedMembers,
  }
}
